import { TextField } from "@material-ui/core";
import { MutationFunction, useMutation, useQuery } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { Field, FieldArray, Form, Formik } from "formik";
import { useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useLocation } from "react-router-dom";
import CustomButtonSimple from "../../../components/CustomMiscellaneous/CustomButtonSimple";
import { CustomErrorMessage } from "../../../components/FormComponents/CustomErrorMessage";
import { FormFieldWrapperColumn } from "../../../components/FormComponents/FieldWrapperColumn/FieldWrapperColumn";
import { hasError } from "../../../components/FormComponents/utils";
import CustomDatePicker from "../../../form/CustomInput/CustomDatePicker/CustomDatePicker";
import { genericGet, genericPatch, genericPost } from "../../../services/api.service";
import { IEntrega, IObra, IPeca } from "../../Obras/newObra/IObra";
import { PecasParaEntregaForm } from "./PecasParaEntregaForm";
import PublishIcon from '@mui/icons-material/Publish';
import CloseIcon from '@mui/icons-material/Close';

export function EntregasForm({ entrega, onCancel }: { entrega?: IEntrega, onCancel: Function }) {

    const location = useLocation();
    const formRef = useRef<any>();
    const [pecas, setPecas] = useState<IPeca[]>([]);
    const currentPath = location.pathname.split('/');

    const isEditingExisting = currentPath.length > 3; 
    
    const { data: obra } = useQuery<IObra>({
        queryKey: ['obra', currentPath],
        queryFn: async () => {
            return await genericGet(`/${currentPath[1]}/${currentPath[2]}`);
        },
        onSuccess: (data : IObra) => {
            console.log("Setting pecas: ", data?.pecas);
            setPecas(data.pecas);
        }
    })

    const formInitialValues: Partial<IEntrega> = useMemo(()=> ({
        id: entrega?.id,
        realDeliveryDate: entrega?.realDeliveryDate ? entrega.realDeliveryDate : new Date(),
        publicDeliveryDate: entrega?.publicDeliveryDate ? entrega.publicDeliveryDate : new Date(),
        productionReadyDate: entrega?.productionReadyDate ? entrega.productionReadyDate : new Date(),
        percentage: entrega?.percentage ?? 100,
        pecasParaEntrega: entrega?.pecasParaEntrega || []
    }), [entrega])

    useEffect(() => {
        if(!isEditingExisting) {
            updatePecasFromOrcamento();
            setPecas(obra?.pecas ?? []);
        }
        
    }, [obra])

    const updatePecasFromOrcamento = () => {

        const currentPecasManager: any = formRef?.current;

        if(currentPecasManager) {
            const pecas = obra?.pecas?.map((p: IPeca) => {
                return {
                    peca: p,
                    quantidade: p.amount
                }
            })
            
            currentPecasManager.setFieldValue('pecasParaEntrega', pecas);
        }  
    }

    const mutationMethod = (): MutationFunction<AxiosResponse<any, any>, any> => {
        const pathPieces = location.pathname.split('/');
        if (!isEditingExisting) return genericPost(`${location.pathname}/entregas`);
        return genericPatch<IEntrega>(`${location.pathname}`);
    };


    const mutation = useMutation(mutationMethod(), {
        onSuccess: () => {
            toast.success("Entrega submetida com sucesso!");
            onCancel();
        },
        onError: () => {
            toast.error("Erro ao submeter entrega")
            console.log("ERR!!!")
        }
    });



    return (

        <div className="border rounded-md shadow-md p-8 px-16 text-sm m-10">
            <Formik
                initialValues={formInitialValues}
                innerRef={formRef}
                // mutation.mutate(({ ...values }))
                validateOnChange={false}
                onSubmit={(values) => mutation.mutate(values)}
            >
                {(props) => {
                    const { values, errors, touched, handleChange, setFieldValue } = props;

                    return (
                        <Form>
                            <div className="flex">
                                <FormFieldWrapperColumn label={"Data de entrega"} className="w-1/3">
                                    <>
                                        <Field
                                            className="w-full bg-zinc-300"
                                            name={`realDeliveryDate`}
                                            size="small"
                                            as={CustomDatePicker}
                                            onChange={(value: Date) => {
                                                setFieldValue(`realDeliveryDate`, value)
                                            }}
                                            value={values.realDeliveryDate ? values.realDeliveryDate : new Date()}
                                            error={hasError({ errors, touched, name: 'realDeliveryDate' })}
                                            variant={'outlined'}
                                            label={'Data de entrega'} />
                                        <CustomErrorMessage name={`realDeliveryDate`} />
                                    </>
                                </FormFieldWrapperColumn>
                                <FormFieldWrapperColumn label={"Data de entrega (para colaboradores)"} className="w-1/3">
                                    <>
                                        <Field
                                            className="w-full bg-zinc-300"
                                            name={`publicDeliveryDate`}
                                            size="small"
                                            as={CustomDatePicker}
                                            onChange={(value: Date) => {
                                                setFieldValue(`publicDeliveryDate`, value)
                                            }}
                                            value={values.publicDeliveryDate ? values.publicDeliveryDate : new Date()}
                                            error={hasError({ errors, touched, name: 'publicDeliveryDate' })}
                                            variant={'outlined'}
                                            label={'Data de entrega (para colaboradores)'} />
                                        <CustomErrorMessage name={`publicDeliveryDate`} />
                                    </>
                                </FormFieldWrapperColumn>
                                <FormFieldWrapperColumn label={"Data de fim de produção"} className="w-1/3">
                                    <>
                                        <Field
                                            className="w-full bg-zinc-300"
                                            name={`productionReadyDate`}
                                            size="small"
                                            as={CustomDatePicker}
                                            onChange={(value: Date) => {
                                                setFieldValue(`productionReadyDate`, value)
                                            }}
                                            value={values.productionReadyDate ? values.productionReadyDate : new Date()}
                                            error={hasError({ errors, touched, name: 'productionReadyDate' })}
                                            variant={'outlined'}
                                            label={'Data de fim de produção'} />
                                        <CustomErrorMessage name={`productionReadyDate`} />
                                    </>
                                </FormFieldWrapperColumn>
                            </div>

                            <FormFieldWrapperColumn label={"Percentagem"} className={"w-1/2"} >
                                <>
                                    <Field
                                        className="w-full"
                                        name={`percentage`}
                                        as={TextField}
                                        error={hasError({ errors, touched, name: 'percentage' })}
                                        variant={'outlined'}
                                        type={'number'}
                                        label={'Percentagem'} />
                                    <CustomErrorMessage name={"percentage"} />
                                </>
                            </FormFieldWrapperColumn>

                            <div>
                                <FieldArray
                                    name={"pecasParaEntrega"}
                                    render={arrayHelpers => <PecasParaEntregaForm formikProps={props} arrayHelpers={arrayHelpers} pecas={pecas} />}
                                />
                            </div>

                            <div className="flex justify-end mt-3 mr-2">
                                <CustomButtonSimple className="mr-2" text={"Cancelar"} Icon={CloseIcon} onClick={() => onCancel(false)} />

                                <CustomButtonSimple type="submit" text={"Submeter"} Icon={PublishIcon} />
                            </div>

                        </Form>
                    )


                }}



            </Formik>
        </div>)


}